/**
 * Watchlist shape — the substrate-bearing reading of the viewer's list.
 *
 * Three substrate-positions and a "set down" disclosure. The viewer is not
 * a workflow unit moving titles through stages; they are a body reaching
 * toward, in rhythm with, and accumulating from films across time.
 *
 *   Carrying toward  — pre-encounter rhythm (want_to_watch)
 *   In rhythm        — active engagement (watching, caught_up, waiting)
 *   Landed           — substantive substrate-engagement that landed
 *                       (scan-feedback verdict === 'landed', not dropped)
 *   Set down         — finished or stopped without landed feedback
 *
 * Reads existing SmartWatchlistItem data with no migration. Closes
 * D3 Continuity by composing two existing data sources (watchlist +
 * scan-feedback aggregator) into a new reflection.
 */

import type { SmartWatchlistItem } from '../types';
import { getAllFeedback, getLandedClusterCounts } from './scanFeedback';

export type WatchlistSectionKey = 'carrying' | 'rhythm' | 'landed' | 'set_down';

export interface WatchlistSection {
  key: WatchlistSectionKey;
  label: string;
  description: string;
  items: SmartWatchlistItem[];
}

export interface ClusterShapeEntry {
  cluster: string;
  count: number;
}

const RHYTHM_STATUSES = new Set([
  'watching',
  'caught_up',
  'waiting_new_season',
  'waiting_new_movie',
]);

const SET_DOWN_STATUSES = new Set(['completed', 'dropped']);

function feedbackKey(mediaType: 'movie' | 'tv', mediaId: number): string {
  return `${mediaType}_${mediaId}`;
}

function watchlistItemFeedbackKey(item: SmartWatchlistItem): string {
  const normalizedType = item.type === 'anime' ? 'tv' : item.type;
  return `${normalizedType}_${item.tmdbId}`;
}

function sortByPriority(a: SmartWatchlistItem, b: SmartWatchlistItem): number {
  if (a.priority === 'goat' && b.priority !== 'goat') return -1;
  if (b.priority === 'goat' && a.priority !== 'goat') return 1;
  if (a.priority === 'high' && b.priority !== 'high') return -1;
  if (b.priority === 'high' && a.priority !== 'high') return 1;
  return (b.addedAt || 0) - (a.addedAt || 0);
}

export function buildWatchlistShape(items: SmartWatchlistItem[]): WatchlistSection[] {
  const landedSet = new Set(
    getAllFeedback()
      .filter((record) => record.verdict === 'landed')
      .map((record) => feedbackKey(record.mediaType, record.mediaId)),
  );

  const carrying: SmartWatchlistItem[] = [];
  const rhythm: SmartWatchlistItem[] = [];
  const landed: SmartWatchlistItem[] = [];
  const setDown: SmartWatchlistItem[] = [];

  for (const item of items) {
    const hasLandedFeedback = landedSet.has(watchlistItemFeedbackKey(item));

    if (hasLandedFeedback && item.status !== 'dropped') {
      landed.push(item);
      continue;
    }

    if (item.status === 'want_to_watch') {
      carrying.push(item);
    } else if (RHYTHM_STATUSES.has(item.status)) {
      rhythm.push(item);
    } else if (SET_DOWN_STATUSES.has(item.status)) {
      setDown.push(item);
    }
  }

  return [
    {
      key: 'carrying',
      label: 'Carrying toward',
      description: 'Films you are reaching for. The pre-encounter rhythm.',
      items: [...carrying].sort(sortByPriority),
    },
    {
      key: 'rhythm',
      label: 'In rhythm',
      description: 'Active engagement. Continue, catch up, wait for the next.',
      items: [...rhythm].sort(sortByPriority),
    },
    {
      key: 'landed',
      label: 'Landed',
      description: 'Body-scans you marked as landed. Your accumulated substrate-shape.',
      items: [...landed].sort(sortByPriority),
    },
    {
      key: 'set_down',
      label: 'Set down',
      description: 'Finished or stopped. Available if you need them.',
      items: [...setDown].sort(sortByPriority),
    },
  ];
}

export function getClusterShape(): ClusterShapeEntry[] {
  const counts = getLandedClusterCounts();
  return Object.entries(counts)
    .map(([cluster, count]) => ({ cluster, count }))
    .sort((a, b) => b.count - a.count);
}
